import React from 'react';

/**
 * g2statistics — Logo SVG (icon + wordmark)
 *
 * Concept:
 *  - Chart bars with ascending heights → benchmark / quartis
 *  - Outer rounded square in primary navy → corporate/analytical
 *  - Wordmark: "g2" (slate) + "statistics" (blue) — institutional + tech
 */

const sizeMap = {
  small:   { wrap: 'gap-1.5', icon: 22, text: 'text-base'  },
  default: { wrap: 'gap-2',   icon: 28, text: 'text-xl'    },
  large:   { wrap: 'gap-3',   icon: 44, text: 'text-3xl'   },
  xl:      { wrap: 'gap-3',   icon: 64, text: 'text-5xl'   },
};

const LogoIcon = ({ size = 28, variant = 'dark' }) => {
  // variant: 'dark' (navy bg, blue+white bars) | 'light' (transparent bg, navy+blue bars)
  const bg     = variant === 'light' ? '#FFFFFF' : '#0F172A';
  const stroke = variant === 'light' ? '#0F172A' : 'transparent';
  const bar1   = variant === 'light' ? '#0F172A' : '#FFFFFF';
  const bar2   = '#2563EB';
  const bar3   = variant === 'light' ? '#2563EB' : '#60A5FA';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="g2statistics logo"
      data-testid="logo-icon"
    >
      <rect x="2" y="2" width="60" height="60" rx="14" fill={bg} stroke={stroke} strokeWidth={variant === 'light' ? 2 : 0} />
      {/* Three ascending bars */}
      <rect x="14" y="38" width="9"  height="14" rx="2" fill={bar1} />
      <rect x="27" y="28" width="9"  height="24" rx="2" fill={bar2} />
      <rect x="40" y="16" width="9"  height="36" rx="2" fill={bar3} />
      {/* Trend arrow */}
      <path
        d="M14 28 L26 22 L36 26 L48 12"
        stroke="#2563EB"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={variant === 'light' ? 0.85 : 0.95}
      />
      <circle cx="48" cy="12" r="2.6" fill="#2563EB" />
    </svg>
  );
};

export const Logo = ({ className = '', size = 'default', showText = true, variant }) => {
  const cfg = sizeMap[size] || sizeMap.default;
  // Auto pick icon variant based on parent (dark sidebar → 'dark', light page → 'light')
  const iconVariant = variant || (className.includes('text-white') ? 'dark' : 'light');

  return (
    <div className={`inline-flex items-center ${cfg.wrap} ${className}`} data-testid="logo">
      <LogoIcon size={cfg.icon} variant={iconVariant} />
      {showText && (
        <span className={`font-bold ${cfg.text} tracking-tight leading-none`}>
          <span className={iconVariant === 'dark' ? 'text-white' : 'text-slate-900'}>g2</span>
          <span className="text-blue-500 font-extrabold">statistics</span>
        </span>
      )}
    </div>
  );
};

export { LogoIcon };
export default Logo;
